// src/pages/StateAdminReport/StateHeadDailyReport.jsx
import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  CircularProgress,
  Alert
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';
import { format } from 'date-fns';
import { API_BASE_URL } from '../../constants/constants';
import BackButton from '../../components/BackButton';
import { usePermissions } from '../../context/PermissionContext';
// [CASH-MODE] UPI / Cash filter + per-day split
import PaymentModeFilter from '../../components/PaymentModeFilter';
import PaymentModeTotals from '../../components/PaymentModeTotals';
import { PAYMENT_MODES } from '../../constants/paymentModes';
import { paymentModeParam } from '../../utils/paymentMode';

const StateHeadDailyReport = () => {
  const { can } = usePermissions();
  const [userLevel, setUserLevel] = useState(null);
  const [userId, setUserId] = useState('');
  const [dailyReports, setDailyReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [paymentMode, setPaymentMode] = useState(PAYMENT_MODES.ALL); // [CASH-MODE]
  const [totalsByMode, setTotalsByMode] = useState(null); // [CASH-MODE]

  // Date filters
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 90);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setUserLevel(decoded.level);
        setUserId(decoded.userId);
      } catch (err) {
        console.error('Invalid token', err);
      }
    }
  }, []);


  const fetchDailyReport = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = sessionStorage.getItem('token');
      const res = await axios.post(
        `${API_BASE_URL}/stateAdminReport/dailyReport`,
        {
          startDate,
          endDate,
          userId,
          requesterLevel: userLevel || 1,
          paymentMode: paymentModeParam(paymentMode) // [CASH-MODE]
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (res.status === 200 && res.data && Array.isArray(res.data.dailyReports)) {
        setDailyReports(res.data.dailyReports);
        setTotalsByMode(res.data.totalsByMode || null); // [CASH-MODE]
      } else {
        setDailyReports([]);
        setTotalsByMode(null); // [CASH-MODE]
      }
    } catch (err) {
      console.error('Error fetching state head daily report:', err);
      setError(err.response?.data?.message || err.message || 'Failed to fetch STATE HEAD daily report.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userLevel !== null) {
      fetchDailyReport();
    }
  }, [userLevel, startDate, endDate, paymentMode]); // [CASH-MODE] paymentMode

  // Search Filter by State Head ID / Name / Mobile / Email
  const filteredReports = useMemo(() => {
    if (!searchTerm.trim()) return dailyReports;
    const term = searchTerm.toLowerCase().trim();
    return dailyReports.filter((r) => {
      const shId = (r.stateHeadId || '').toLowerCase();
      const shName = (r.stateHeadName || '').toLowerCase();
      const shMobile = String(r.stateHeadMobile || '').toLowerCase();
      const shEmail = (r.stateHeadEmail || '').toLowerCase();

      return (
        shId.includes(term) ||
        shName.includes(term) ||
        shMobile.includes(term) ||
        shEmail.includes(term)
      );
    });
  }, [dailyReports, searchTerm]);

  // [CASH-MODE] per-day UPI / Cash fields present only on the updated API
  const hasModeSplit = dailyReports.some((r) => r.upiAmount !== undefined || r.cashAmount !== undefined);
  const modeTotals = (!searchTerm.trim() && totalsByMode) || (hasModeSplit
    ? filteredReports.reduce(
        (acc, r) => {
          acc.UPI.amount += Number(r.upiAmount) || 0;
          acc.CASH.amount += Number(r.cashAmount) || 0;
          return acc;
        },
        { UPI: { amount: 0 }, CASH: { amount: 0 } }
      )
    : null);
  const modeAmountKey = modeTotals?.UPI?.dailyTransactionAmount !== undefined
    ? 'dailyTransactionAmount'
    : (modeTotals?.UPI?.totalAmount !== undefined ? 'totalAmount' : 'amount');

  // CSV Export (permission gated)
  const handleExportCSV = () => {
    if (!can('stateHead.daily.export')) return;
    if (filteredReports.length === 0) return;

    const headers = [
      'SL No',
      'Date',
      'State Head ID',
      'State Head Name',
      'Mobile Number',
      'Email',
      'Daily Transactions Count',
      'Daily Transaction Amount (₹)',
      'Daily Commission Amount (₹)',
      // [CASH-MODE]
      ...(hasModeSplit ? ['UPI Amount (₹)', 'Cash Amount (₹)', 'UPI Commission (₹)', 'Cash Commission (₹)'] : [])
    ];

    const csvRows = [headers.join(',')];

    filteredReports.forEach((r, idx) => {
      const formattedDate = r.date ? format(new Date(r.date), 'dd/MM/yyyy') : 'N/A';

      const row = [
        idx + 1,
        `"${formattedDate}"`,
        `"${r.stateHeadId || 'N/A'}"`,
        `"${r.stateHeadName || 'N/A'}"`,
        `"${r.stateHeadMobile || 'N/A'}"`,
        `"${r.stateHeadEmail || 'N/A'}"`,
        r.dailyTransactionCount || 0,
        r.dailyTransactionAmount || 0,
        r.dailyCommission || 0,
        // [CASH-MODE]
        ...(hasModeSplit ? [r.upiAmount || 0, r.cashAmount || 0, r.upiCommission || 0, r.cashCommission || 0] : [])
      ];

      csvRows.push(row.join(','));
    });

    const csvContent = 'data:text/csv;charset=utf-8,' + csvRows.join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `STATE_HEAD_Daily_Report_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box sx={{ p: 3, position: 'relative' }}>
      <BackButton />
      <br /><br />

      {/* Header & Controls */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          STATE HEAD Daily Report
        </Typography>

        {/* CSV Export (permission gated) */}
        {can('stateHead.daily.export') && (
          <Button
            variant="contained"
            color="success"
            startIcon={<DownloadIcon />}
            onClick={handleExportCSV}
            disabled={filteredReports.length === 0}
          >
            Export CSV
          </Button>
        )}
      </Box>

      {/* Filters Bar: Date Wise + Search */}
      <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          {/* Search Input */}
          <Box sx={{ flex: 1, minWidth: 250, display: 'flex', alignItems: 'center', gap: 1 }}>
            <SearchIcon color="action" />
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              placeholder="Search by State Head ID, Name, Mobile, or Email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Box>

          {/* Date Filters */}
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            <TextField
              label="From Date"
              type="date"
              size="small"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="To Date"
              type="date"
              size="small"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Box>

          {/* [CASH-MODE] */}
          <PaymentModeFilter value={paymentMode} onChange={setPaymentMode} />
        </Box>
      </Paper>

      {/* [CASH-MODE] */}
      <PaymentModeTotals totals={modeTotals} amountKey={modeAmountKey} title="Transactions" sx={{ mb: 2 }} />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={3} sx={{ borderRadius: 2, maxHeight: 'calc(100vh - 260px)', minHeight: 450, overflow: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead sx={{ backgroundColor: 'primary.main' }}>
              <TableRow>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>SL No</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Date</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>State Head ID</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>State Head Name</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Daily Transaction (under this State Head)</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Daily Commission (under this State Head)</TableCell>
                {/* [CASH-MODE] */}
                {hasModeSplit && (
                  <>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>UPI (Txn / Comm.)</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Cash (Txn / Comm.)</TableCell>
                  </>
                )}
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredReports.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={hasModeSplit ? 8 : 6} align="center" sx={{ py: 3 }}>
                    No daily report records found for the selected date range.
                  </TableCell>
                </TableRow>
              ) : (
                filteredReports.map((r, idx) => {
                  const formattedDate = r.date ? format(new Date(r.date), 'dd/MM/yyyy') : 'N/A';

                  return (
                    <TableRow key={r.id || idx} hover>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>{formattedDate}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                        {r.stateHeadId || 'N/A'}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>
                        {r.stateHeadName || 'N/A'}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {r.dailyTransactionCount || 0} Txns (₹{(r.dailyTransactionAmount || 0).toLocaleString()})
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ color: 'success.main', fontWeight: 'bold' }}>
                        ₹{(r.dailyCommission || 0).toLocaleString()}
                      </TableCell>
                      {/* [CASH-MODE] */}
                      {hasModeSplit && (
                        <>
                          <TableCell>
                            ₹{(Number(r.upiAmount) || 0).toLocaleString()} / ₹{(Number(r.upiCommission) || 0).toLocaleString()}
                          </TableCell>
                          <TableCell sx={{ color: 'warning.dark' }}>
                            ₹{(Number(r.cashAmount) || 0).toLocaleString()} / ₹{(Number(r.cashCommission) || 0).toLocaleString()}
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default StateHeadDailyReport;
